import { useEffect, useMemo, useRef, useState } from "react";

import { getAvailability } from "@/features/devices/api";
import type {
  PortAvailability,
  ProductionHistoryWindow,
} from "@/features/devices/types";
import { parseTag } from "@/features/machines/addressing";
import type { Machine } from "@/features/machines/types";
import type { DashboardWidget } from "./types";

export type WidgetAvailabilityState =
  | { status: "loading" }
  /**
   * Troca de janela em andamento: `data` é a janela ANTERIOR, mantida na tela
   * para o card nunca ficar em branco enquanto a nova chega.
   */
  | { status: "reloading"; data: PortAvailability }
  | { status: "ready"; data: PortAvailability }
  | { status: "unavailable" }
  | { status: "error" };

interface AvailabilityRequest {
  key: string;
  identity: string;
  configHashHex?: string | null;
  deviceId: string;
  portIndex: number;
  window: ProductionHistoryWindow;
  widgetIds: string[];
}

/**
 * Busca a disponibilidade (rodando/parado/utilização por janela) dos widgets
 * `utilization` via `/api/devices/:id/availability`. Mesma estrutura do
 * `useProductionHistories`: agrupa por device/porta/janela, atualiza a cada
 * 60s e cancela em flight ao trocar de assinatura.
 */
export function useAvailability(
  machine: Machine,
  widgets: DashboardWidget[],
  configHashesByDevice?: ReadonlyMap<string, string | null>,
  enabled = true,
): Map<string, WidgetAvailabilityState> {
  const requests = useMemo(
    () => buildAvailabilityRequests(machine, widgets, configHashesByDevice),
    [machine, widgets, configHashesByDevice],
  );
  const signature = useMemo(
    () => requests.map((request) => `${request.key}:${request.widgetIds.join(",")}`).join("|"),
    [requests],
  );
  const [states, setStates] = useState<Map<string, WidgetAvailabilityState>>(
    () => new Map(),
  );
  // Mesmo padrão do useProductionHistories: cache por device:porta:janela para
  // troca instantânea de volta, e última janela boa por widget para o estado
  // `reloading` (dado antigo visível, nunca card em branco).
  const cacheRef = useRef<Map<string, PortAvailability>>(new Map());
  const lastByWidgetRef = useRef<Map<string, { identity: string; data: PortAvailability }>>(new Map());
  const activity = useMemo(() => ({ enabled, signature }), [enabled, signature]);
  const [stateActivity, setStateActivity] = useState<object | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (requests.length === 0) {
      setStates(new Map());
      return;
    }

    let active = true;
    let controller = new AbortController();
    let pending = false;
    let queued = false;

    const load = async (showLoading: boolean) => {
      if (!active) return;
      if (pending) { queued = true; return; }
      pending = true;
      controller = new AbortController();
      if (showLoading) {
        setStateActivity(activity);
        setStates((current) => {
          const next = new Map(current);
          for (const request of requests) {
            const cached = cacheRef.current.get(request.key);
            if (cached) {
              next.set(request.key, { status: "reloading", data: cached });
              continue;
            }
            const previous = request.widgetIds.map((id) => lastByWidgetRef.current.get(id))
              .find((entry) => entry?.identity === request.identity);
            next.set(request.key, previous
              ? { status: "reloading", data: previous.data } : { status: "loading" });
          }
          return next;
        });
      }

      await Promise.all(
        requests.map(async (request) => {
          try {
            const data = await getAvailability(
              request.deviceId,
              request.portIndex,
              request.window,
              controller.signal,
            );
            if (!active) return;
            if (data.deviceId !== request.deviceId || data.portIndex !== request.portIndex || data.window !== request.window) {
              cacheRef.current.delete(request.key);
              for (const id of request.widgetIds) lastByWidgetRef.current.delete(id);
              setStates((current) => assignState(current, [request.key], { status: "error" }));
              return;
            }
            if (data.configHashHex && data.configHashHex !== request.configHashHex) {
              cacheRef.current.delete(request.key);
              for (const id of request.widgetIds) lastByWidgetRef.current.delete(id);
              setStates((current) => assignState(current, [request.key], { status: "loading" }));
              return;
            }
            cacheRef.current.set(request.key, data);
            for (const widgetId of request.widgetIds) {
              lastByWidgetRef.current.set(widgetId, { identity: request.identity, data });
            }
            setStates((current) => assignState(current, [request.key], { status: "ready", data }));
          } catch (error) {
            if (!active || (error as { name?: string }).name === "AbortError") return;
            const status = (error as { status?: number }).status;
            const nextState: WidgetAvailabilityState =
              status === 503 ? { status: "unavailable" } : { status: "error" };
            cacheRef.current.delete(request.key);
            for (const id of request.widgetIds) lastByWidgetRef.current.delete(id);
            setStates((current) => assignState(current, [request.key], nextState));
          }
        }),
      );
      pending = false;
      if (active && queued) {
        queued = false;
        void load(false);
      }
    };

    void load(true);
    const interval = window.setInterval(() => void load(false), 60_000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
    };
    // `signature` serializa `requests` (deviceId/porta/janela/ids) e é a chave
    // de dedupe deliberada — depender de `requests` re-rodaria o efeito a cada
    // render sem necessidade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled, activity]);

  // Projeta pela identidade atual já no render, sem frame da porta anterior.
  const byWidget = new Map<string, WidgetAvailabilityState>();
  for (const request of requests) {
    const state = states.get(request.key);
    const projected = enabled && stateActivity === activity ? state :
      state && "data" in state ? { status: "reloading" as const, data: state.data } : undefined;
    for (const id of request.widgetIds) byWidget.set(id, projected ?? { status: "loading" });
  }
  return byWidget;
}

export function buildAvailabilityRequests(
  machine: Machine,
  widgets: DashboardWidget[],
  configHashesByDevice?: ReadonlyMap<string, string | null>,
): AvailabilityRequest[] {
  const byKey = new Map<string, AvailabilityRequest>();

  for (const widget of widgets) {
    if (widget.widgetType !== "utilization") continue;
    const address = parseTag(widget.source);
    if (!address) continue;
    const deviceId = machine.slots.find(
      (slot) => slot.slotIndex === address.slotIndex,
    )?.deviceId;
    if (!deviceId) continue;

    const window = widget.settings.history?.window ?? "24h";
    const configHashHex = configHashesByDevice?.get(deviceId);
    const identity = `${machine.workspaceId}:${deviceId}:${address.portIndex}:${configHashHex ?? "unknown"}`;
    const key = `${identity}:${window}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.widgetIds.push(widget.id);
    } else {
      byKey.set(key, {
        key,
        identity,
        configHashHex,
        deviceId,
        portIndex: address.portIndex,
        window,
        widgetIds: [widget.id],
      });
    }
  }

  return [...byKey.values()];
}

function assignState(
  current: Map<string, WidgetAvailabilityState>,
  widgetIds: string[],
  state: WidgetAvailabilityState,
): Map<string, WidgetAvailabilityState> {
  const next = new Map(current);
  for (const widgetId of widgetIds) next.set(widgetId, state);
  return next;
}
